import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { AlchamisMark } from "@/components/AlchamisMark";

const SESSION_KEY = "alchamis_concierge_session";

export default function Concierge() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Welcome. I am the AlchamisGroup Executive Concierge. How may I assist with your strategic, technology, or AI priorities today?",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionId = useRef(localStorage.getItem(SESSION_KEY) || null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  async function handleSend(e) {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setSending(true);

    try {
      const res = await fetch(`${API_BASE}/concierge/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId.current }),
      });
      if (!res.ok || !res.body) {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: "Concierge is temporarily unavailable." };
          return copy;
        });
        setSending(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let assistantContent = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const ev of events) {
          const line = ev.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const data = JSON.parse(line.slice(5).trim());
            if (data.type === "session") {
              sessionId.current = data.session_id;
              localStorage.setItem(SESSION_KEY, data.session_id);
            } else if (data.type === "delta") {
              assistantContent += data.content;
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: assistantContent };
                return copy;
              });
            } else if (data.type === "error") {
              assistantContent += "\n\n(" + data.message + ")";
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: assistantContent };
                return copy;
              });
            }
          } catch (err) {
            // ignore malformed chunk
          }
        }
      }
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Concierge connection interrupted." };
        return copy;
      });
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      {!open && (
        <button
          data-testid="concierge-launch-button"
          onClick={() => setOpen(true)}
          className="fixed z-[60] right-6 bottom-24 sm:bottom-6 sm:right-24 group flex items-center gap-3 px-5 py-4 bg-[#001B54] border border-gold/40 hover:border-gold transition-all duration-500 shadow-[0_20px_60px_-20px_rgba(212,175,55,0.5)]"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-70"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-gold"></span>
          </span>
          <span className="font-display text-sm text-white tracking-[0.18em] uppercase">Ask Alchamis</span>
        </button>
      )}

      {open && (
        <div
          data-testid="concierge-panel"
          className="fixed z-[60] right-4 bottom-24 sm:right-24 sm:bottom-6 w-[calc(100vw-2rem)] sm:w-[420px] h-[600px] max-h-[80vh] flex flex-col bg-[#001B54] border border-gold/30 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.8)]"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-gold/15">
            <div className="flex items-center gap-3">
              <AlchamisMark size={28} />
              <div>
                <div className="text-xs tracking-[0.22em] uppercase text-gold">Executive Concierge</div>
                <div className="font-display text-base text-white">Ask Alchamis</div>
              </div>
            </div>
            <button
              data-testid="concierge-close"
              onClick={() => setOpen(false)}
              className="text-white/60 hover:text-white p-1.5"
              aria-label="Close concierge"
            >
              <X size={18} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-6 space-y-4">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`concierge-message-${m.role}`}
                className={`text-sm leading-relaxed ${m.role === "user" ? "text-right" : "text-left"}`}
              >
                {m.role === "assistant" && i > 0 && (
                  <div className="text-[0.6rem] tracking-[0.3em] text-gold/70 mb-1.5">CONCIERGE</div>
                )}
                {m.role === "user" && (
                  <div className="text-[0.6rem] tracking-[0.3em] text-white/40 mb-1.5">YOU</div>
                )}
                <div
                  className={`inline-block px-4 py-3 border ${
                    m.role === "user"
                      ? "bg-gold/10 border-gold/30 text-white"
                      : "bg-white/[0.04] border-white/10 text-white/90"
                  } whitespace-pre-wrap max-w-[90%]`}
                >
                  {m.content || (sending && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={handleSend} className="border-t border-gold/15 p-3 flex items-center gap-2">
            <input
              data-testid="concierge-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about a service or solution…"
              className="flex-1 bg-[#000B24] border border-white/10 focus:border-gold/60 outline-none px-3 py-3 text-sm text-white placeholder:text-white/30"
              disabled={sending}
            />
            <button
              data-testid="concierge-send"
              type="submit"
              disabled={sending || !input.trim()}
              className="px-4 py-3 bg-gold text-navy disabled:opacity-50 hover:bg-gold-pale transition-colors"
              aria-label="Send"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
